import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../plannerApi';
import { NeedsHousehold, PageHeader, SectionTitle, useConfirm, useHousehold } from '../common';
import { ADULT_WEIGHT, CHILD_WEIGHT, MEMBER_PALETTE, avatarColor, number, parseNumber, shortDateTime } from '../labels';

type Portion = 'adult' | 'child' | 'custom';

/** Add (/pessoa/nova) or edit (/pessoa/:memberId) a person of the household. */
const MemberForm: React.FC = () => {
  const { memberId } = useParams();
  const { members, set } = useHousehold();
  const navigate = useNavigate();
  const [confirm, confirmDialog] = useConfirm();
  const member = memberId ? members.find(m => m.publicId === memberId) : undefined;

  const [name, setName] = useState('');
  const [portion, setPortion] = useState<Portion>('adult');
  const [custom, setCustom] = useState('');
  const [color, setColor] = useState(MEMBER_PALETTE[0]);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!member) return;
    setName(member.name);
    const p: Portion = member.portionWeight === ADULT_WEIGHT ? 'adult' : member.portionWeight === CHILD_WEIGHT ? 'child' : 'custom';
    setPortion(p);
    setCustom(p === 'custom' ? number(member.portionWeight, 2) : '');
    setColor(avatarColor(member));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.publicId]);

  const back = () => navigate('/casa/agregado');

  if (memberId && !member) {
    return <Alert severity="warning" action={<Button color="inherit" onClick={back}>Voltar</Button>}>Esta pessoa já não está na casa.</Alert>;
  }

  const save = async () => {
    if (!name.trim()) { setError('Indica o nome.'); return; }
    let weight = ADULT_WEIGHT;
    if (portion === 'child') weight = CHILD_WEIGHT;
    if (portion === 'custom') {
      const parsed = parseNumber(custom);
      if (parsed === null || parsed <= 0) { setError('Indica uma dose válida (ex.: 0,75).'); return; }
      weight = parsed;
    }
    const linkEmail = member?.hasAccount ? null : email.trim() || null;
    const body = { name: name.trim(), portionWeight: weight, color, email: linkEmail };
    setError(null);
    setBusy(true);
    try {
      set(member ? await api.updateMember(member.publicId, body) : await api.addMember(body));
      back();
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  const cancelInvitation = async () => {
    if (!member) return;
    setError(null);
    setBusy(true);
    try {
      set(await api.cancelInvitation(member.publicId));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!member) return;
    if (!await confirm({ title: 'Remover', text: `Remover ${member.name} da casa?`, confirmText: 'Remover', danger: true })) return;
    setError(null);
    setBusy(true);
    try {
      set(await api.removeMember(member.publicId));
      back();
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={back} sx={{ mb: 1 }}>Casa e pessoas</Button>
      <PageHeader title={member ? 'Editar pessoa' : 'Nova pessoa'} />
      <Stack spacing={2} component="form" onSubmit={e => { e.preventDefault(); save(); }}>
        <TextField label="Nome" placeholder="Ex.: Tomás" value={name} onChange={e => setName(e.target.value)} autoFocus={!member} />
        <FormControl>
          <InputLabel id="portion-label">Dose</InputLabel>
          <Select labelId="portion-label" label="Dose" value={portion} onChange={e => setPortion(e.target.value as Portion)}>
            <MenuItem value="adult">Adulto · 1 dose</MenuItem>
            <MenuItem value="child">Criança · ½ dose</MenuItem>
            <MenuItem value="custom">Outra</MenuItem>
          </Select>
        </FormControl>
        {portion === 'custom' && (
          <TextField label="Dose" placeholder="Ex.: 0,75" value={custom} onChange={e => setCustom(e.target.value)} slotProps={{ htmlInput: { inputMode: 'decimal' } }} />
        )}

        <Box>
          <SectionTitle>Cor</SectionTitle>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
            {MEMBER_PALETTE.map(hex => {
              const selected = hex.toLowerCase() === color.toLowerCase();
              return (
                <Box
                  key={hex}
                  component="button"
                  type="button"
                  aria-label={`Cor ${hex}`}
                  aria-pressed={selected}
                  onClick={() => setColor(hex)}
                  sx={{
                    width: 36, height: 36, borderRadius: '50%', bgcolor: hex, cursor: 'pointer', p: 0,
                    border: 3, borderColor: selected ? 'text.primary' : 'transparent',
                  }}
                />
              );
            })}
          </Box>
        </Box>

        <Box>
          <SectionTitle>Conta</SectionTitle>
          {member?.hasAccount ? (
            <Typography variant="body2" color="text.secondary">
              {member.isCurrentUser ? 'Esta pessoa és tu.' : `Associada a ${member.email}.`}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {member?.pendingInvitation && (
                <Alert
                  severity="info"
                  action={<Button color="inherit" size="small" onClick={cancelInvitation} disabled={busy}>Cancelar convite</Button>}
                >
                  Convite enviado a {member.pendingInvitation.email}, válido até {shortDateTime(member.pendingInvitation.expiresAt)}.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                Opcional. Indica o email da conta dessa pessoa para a convidar: o convite aparece-lhe quando entrar
                na app e, se o aceitar, passa também a ver e editar a casa.
              </Typography>
              <TextField
                type="email"
                placeholder="email@exemplo.com"
                label={member?.pendingInvitation ? 'Convidar outro email' : 'Email'}
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </Stack>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button type="submit" variant="contained" disabled={busy}>Guardar</Button>
          {member && <Button variant="outlined" color="error" onClick={remove} disabled={busy}>Remover da casa</Button>}
        </Stack>
      </Stack>
      {confirmDialog}
    </Box>
  );
};

const HouseholdMemberEditPage: React.FC = () => <NeedsHousehold><MemberForm /></NeedsHousehold>;

export default HouseholdMemberEditPage;
